import {
  Alert,
  App,
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Tooltip,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useCorrectionsCancelMovement,
  useCorrectionsEditReceipt,
} from '@/api/generated/endpoints/corrections/corrections';
import { useStockGetBatch } from '@/api/generated/endpoints/stock/stock';
import type {
  AuditEntryOut,
  AuditEntryOutCorrectableKindsItem as CorrectionKind,
  BatchDetail,
  ReceiptEditIn,
} from '@/api/generated/model';
import { confirmAction } from '@/components/confirmAction';
import { QtyInput } from '@/components/qty';
import { problemMessage } from '@/i18n/problem';
import { compareDecimals } from '@/lib/decimal';
import { qtyRule, type QtyValue } from '@/lib/qty';
import { qtyText } from '@/lib/units';
import { ChangeClientModal, ShipmentStatusModal } from '@/pages/audit/ShipmentCorrections';
import { useCorrectionRefresh } from '@/pages/audit/useCorrectionRefresh';

const DATE = 'DD.MM.YYYY';
const API_DATE = 'YYYY-MM-DD';
const DATETIME = 'DD.MM.YYYY HH:mm';
const REASON_RULES = [{ required: true, whitespace: true, min: 3 }];

/** Кнопки исправления у записи журнала (только админ, 48 часов — ADR-0012). */
export function CorrectionActions({ entry }: { entry: AuditEntryOut }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<CorrectionKind | null>(null);
  if (!entry.correctable_until || dayjs(entry.correctable_until).isBefore(dayjs())) return null;
  const until = t('corrections.until', { at: dayjs(entry.correctable_until).format(DATETIME) });
  const close = () => {
    setOpen(null);
  };
  return (
    <Space size={4} wrap>
      {entry.correctable_kinds.map((kind) => (
        <Tooltip key={kind} title={until}>
          <Button
            size="small"
            onClick={() => {
              setOpen(kind);
            }}
          >
            {t(`corrections.button.${kind}`)}
          </Button>
        </Tooltip>
      ))}
      {open === 'edit_receipt' && <ReceiptModal entry={entry} onClose={close} />}
      {open === 'cancel_movement' && <CancelModal entry={entry} onClose={close} />}
      {open === 'change_client' && <ChangeClientModal entry={entry} onClose={close} />}
      {(open === 'undo_acceptance' || open === 'restore_cancelled') && (
        <ShipmentStatusModal entry={entry} kind={open} onClose={close} />
      )}
    </Space>
  );
}

function CancelModal({ entry, onClose }: { entry: AuditEntryOut; onClose: () => void }) {
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const [form] = Form.useForm<{ reason: string }>();
  const [error, setError] = useState<string | null>(null);
  const refresh = useCorrectionRefresh();
  const cancel = useCorrectionsCancelMovement();
  const movementId = Number(entry.key.split(':')[1]);
  const uom = entry.product?.base_uom ?? 'kg';
  // Отмена возвращает то, что движение забрало: −2 кг списания → +2 кг в партию.
  const back = entry.qty?.startsWith('-') ? entry.qty.slice(1) : `-${entry.qty ?? '0'}`;
  const what = entry.kind === 'writeoff' ? 'writeoff' : 'adjustment';

  async function submit({ reason }: { reason: string }) {
    setError(null);
    const ok = await confirmAction(modal, {
      title: t(`corrections.confirmCancelTitle.${what}`),
      details: [
        t('corrections.confirmWho', { who: entry.actor?.name ?? '—' }),
        t('corrections.confirmBatch', {
          product: entry.product?.name ?? '',
          id: entry.batch_id,
        }),
        t('corrections.confirmReturn', { qty: qtyText(t, back, uom) }),
        t('corrections.confirmReason', { reason: reason.trim() }),
      ],
      okText: t('corrections.confirmCancelOk', { qty: qtyText(t, back, uom) }),
      cancelText: t('common.cancel'),
    });
    if (!ok) return;
    try {
      await cancel.mutateAsync({ movementId, data: { reason: reason.trim() } });
      void message.success(t('corrections.done'));
      await refresh();
      onClose();
    } catch (e) {
      setError(problemMessage(e));
    }
  }

  return (
    <Modal
      open
      title={t(`corrections.cancelTitle.${what}`)}
      okText={t('corrections.next')}
      onOk={() => {
        form.submit();
      }}
      onCancel={onClose}
      okButtonProps={{ loading: cancel.isPending, danger: true }}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" onFinish={(v) => void submit(v)}>
        <Form.Item
          name="reason"
          label={t('corrections.reason')}
          extra={t('corrections.reasonHint')}
          rules={REASON_RULES.map((r) => ({ ...r, message: t('corrections.reasonRequired') }))}
        >
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} maxLength={2000} />
        </Form.Item>
      </Form>
      {error && <Alert type="error" showIcon message={error} />}
    </Modal>
  );
}

interface ReceiptValues {
  qty: QtyValue;
  expires_on: Dayjs | null;
  country: string;
  caliber: string;
  supplier: string;
  comment: string;
  pallets: number | null;
  reason: string;
}

const TEXT_FIELDS = ['country', 'caliber', 'supplier', 'comment'] as const;

/** Только изменённые поля: незатронутое не уходит на сервер и не попадает в «было/стало». */
function changedFields(b: BatchDetail, v: ReceiptValues): Omit<ReceiptEditIn, 'reason'> {
  const out: Omit<ReceiptEditIn, 'reason'> = {};
  if (typeof v.qty === 'string' && compareDecimals(v.qty, b.qty_received, 3) !== 0) out.qty = v.qty;
  const expires = v.expires_on ? v.expires_on.format(API_DATE) : null;
  if (expires !== (b.expires_on ?? null)) out.expires_on = expires;
  for (const f of TEXT_FIELDS) {
    const next = v[f].trim() || null;
    if (next !== (b[f] ?? null)) out[f] = next;
  }
  if ((v.pallets ?? null) !== (b.pallets ?? null)) out.pallets = v.pallets ?? null;
  return out;
}

function ReceiptModal({ entry, onClose }: { entry: AuditEntryOut; onClose: () => void }) {
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const [form] = Form.useForm<ReceiptValues>();
  const [error, setError] = useState<string | null>(null);
  const refresh = useCorrectionRefresh();
  const edit = useCorrectionsEditReceipt();
  const batchId = entry.batch_id ?? 0;
  const batch = useStockGetBatch(batchId);
  const b = batch.data;

  async function submit(v: ReceiptValues) {
    if (!b) return;
    setError(null);
    const changes = changedFields(b, v);
    const keys = Object.keys(changes) as (keyof typeof changes)[];
    if (keys.length === 0) {
      setError(t('corrections.nothingChanged'));
      return;
    }
    const uom = b.product.base_uom;
    const show = (k: keyof typeof changes, value: string | number | null | undefined) => {
      if (value === null || value === undefined || value === '') return '—';
      if (typeof value === 'number') return String(value);
      if (k === 'qty') return qtyText(t, value, uom);
      if (k === 'expires_on') return dayjs(value).format(DATE);
      return value;
    };
    const before: Record<keyof typeof changes, string | number | null> = {
      qty: b.qty_received,
      expires_on: b.expires_on ?? null,
      country: b.country ?? null,
      caliber: b.caliber ?? null,
      supplier: b.supplier ?? null,
      comment: b.comment ?? null,
      pallets: b.pallets,
    };
    const ok = await confirmAction(modal, {
      title: t('corrections.confirmReceiptTitle', { id: b.id }),
      details: [
        t('corrections.confirmWho', { who: entry.actor?.name ?? '—' }),
        ...keys.map((k) =>
          t('corrections.confirmField', {
            field: t(`corrections.field.${k}`),
            before: show(k, before[k]),
            after: show(k, changes[k]),
          }),
        ),
        t('corrections.confirmReason', { reason: v.reason.trim() }),
      ],
      okText: t('corrections.confirmReceiptOk'),
      cancelText: t('common.cancel'),
    });
    if (!ok) return;
    try {
      await edit.mutateAsync({ batchId, data: { ...changes, reason: v.reason.trim() } });
      void message.success(t('corrections.done'));
      await refresh();
      onClose();
    } catch (e) {
      setError(problemMessage(e));
    }
  }

  return (
    <Modal
      open
      title={t('corrections.receiptTitle', { id: batchId })}
      okText={t('corrections.next')}
      onOk={() => {
        form.submit();
      }}
      onCancel={onClose}
      okButtonProps={{ loading: edit.isPending, disabled: !b }}
      destroyOnHidden
    >
      {b && (
        <Form<ReceiptValues>
          form={form}
          layout="vertical"
          initialValues={{
            qty: b.qty_received,
            expires_on: b.expires_on ? dayjs(b.expires_on) : null,
            country: b.country ?? '',
            caliber: b.caliber ?? '',
            supplier: b.supplier ?? '',
            comment: b.comment ?? '',
            pallets: b.pallets,
            reason: '',
          }}
          onFinish={(v) => void submit(v)}
        >
          <Form.Item
            name="qty"
            label={t('corrections.field.qty')}
            rules={[qtyRule({ required: true, positive: true })]}
          >
            <QtyInput uom={b.product.base_uom} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="expires_on" label={t('corrections.field.expires_on')}>
            <DatePicker format={DATE} />
          </Form.Item>
          <Form.Item
            name="pallets"
            label={t('corrections.field.pallets')}
            extra={t('corrections.palletsHint')}
          >
            <InputNumber min={1} max={999} precision={0} />
          </Form.Item>
          {TEXT_FIELDS.map((f) => (
            <Form.Item key={f} name={f} label={t(`corrections.field.${f}`)}>
              <Input maxLength={f === 'comment' ? 2000 : f === 'supplier' ? 300 : 100} />
            </Form.Item>
          ))}
          <Form.Item
            name="reason"
            label={t('corrections.reason')}
            extra={t('corrections.reasonHint')}
            rules={REASON_RULES.map((r) => ({ ...r, message: t('corrections.reasonRequired') }))}
          >
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} maxLength={2000} />
          </Form.Item>
        </Form>
      )}
      {error && <Alert type="error" showIcon message={error} />}
    </Modal>
  );
}
